import { useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { AssigneeRow } from "../primitives/AssigneeRow";
import { Button } from "../primitives/Button";
import { Card } from "../primitives/Card";
import { TextField } from "../primitives/TextField";
import { useProfileNames } from "../primitives/useProfileNames";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { AuthoredAs } from "./types";
import type { UiTheme } from "../theme";

// PE-48 — the assignee panel for the scheduler-composer, mounted once here
// and consumed by ProgramComposerBody, PlanComposer.tsx's metaBand and
// AssignSheet. Semantics come from the legacy web editor, removed with RW-14
// (7ae8e35e:apps/web/src/programs/ProgramEditor.tsx:386-467): current assignees as
// rows, the caller's own sub never removable, roster-pick as the PRIMARY
// add path, email as a secondary fallback (resolveAssignees on the server
// requires an active coach edge even for the email path, so a roster pick is
// the only add that can never 403 at save time).
//
// The ROSTER is INJECTED (`athleteRoster`), never fetched here — the shared
// groupCoachEdges().athletesActive rule (CN-coach-edges-fetch): mobile reads
// its app-wide CoachEdgesContext, web-rnw threads AppShell's single
// useCoachStatus read down through PlanScreen. DISPLAY NAME resolution
// (GET /api/profiles/:sub, `name ?? fallbackName`) is the shared
// useProfileNames primitive (packages/ui/src/primitives/useProfileNames.ts)
// — not a second way of naming an athlete, the same one LibraryCoachBadge
// calls with a one-element array. useCoachDashboard.ts's own name read is a
// third, deliberately different shape (one leg of its own 5-way fan-out per
// athlete) — see next/consolidation.md.
export type ComposerAssigneesBandProps = {
  theme: UiTheme;
  apiFetch: ProgramApiFetch;
  /** Picks the i18n namespace: "program" or "plans" already carry the
   *  assignTo/assignToHelp/assignSelf/addAssignee/removeAssignee/
   *  assigneePlaceholder keys (en.json:682-687 / :880-884). */
  kind: "program" | "plans";
  /** CO-20 — which gesture this mount speaks: "coach" keeps today's
   *  Assegna copy (assignTo/assignToHelp/…), "athlete" swaps in the
   *  Condividi-flavoured copy (shareTo/shareToHelp/…) via `gk` below.
   *  Parallel prop to `kind`, same "which i18n keys" job, orthogonal
   *  axis — `kind` picks the namespace (program vs plans), this picks
   *  the gesture inside it. Absent ⇒ "athlete" (Condividi), matching the
   *  shared schema's own default. */
  authoredAs?: AuthoredAs;
  // Typed `string | null` for coherence with the Library's own callerSub
  // (useLibrary.ts) and ProgramComposerProps.callerSub above it — the
  // caller always narrows to a real, non-empty string before mounting this
  // band (`callerSub ? <ComposerAssigneesBand …> : null`, a truthy test),
  // so every `!== callerSub` comparison below still means exactly what it
  // always meant.
  callerSub: string | null;
  /** Active-athlete subs this coach may assign to (already filtered +
   *  grouped by the host via groupCoachEdges().athletesActive).
   *  CO-20 — no longer part of the mount gate (only `callerSub` is): an
   *  athlete-view mount with nothing here still renders, with `authoredAs`
   *  switching the copy to the share-empty state instead of the roster
   *  one. Always a concrete array, never undefined — the two composers
   *  pass `athleteRoster ?? []`. */
  athleteRoster: ReadonlyArray<string>;
  assignedTo: readonly string[];
  assigneeEmails: readonly string[];
  onChangeAssignedTo: (next: string[]) => void;
  onChangeAssigneeEmails: (next: string[]) => void;
};

export function ComposerAssigneesBand({
  theme,
  apiFetch,
  kind,
  authoredAs = "athlete",
  callerSub,
  athleteRoster,
  assignedTo,
  assigneeEmails,
  onChangeAssignedTo,
  onChangeAssigneeEmails,
}: ComposerAssigneesBandProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  // Named `tk` (translate-key), not `key`: this file also hands React a JSX
  // `key` prop on every mapped row, and the two must not be confused.
  const tk = (k: string) => `${kind}.${k}`;
  // CO-20 — picks between the Assegna and Condividi base key for the
  // handful of strings whose wording actually differs by gesture (title,
  // help, and the roster/candidates section's three copy states). Every
  // other string in this file (Add/Remove/email flow/pending caption)
  // reads the same regardless of who's asking, so it stays on `tk` alone.
  const gk = (assignKey: string, shareKey: string) =>
    tk(authoredAs === "coach" ? assignKey : shareKey);

  // Every OTHER sub this band needs a name for: the assigned athletes (self
  // excluded — self renders as "You") plus every roster candidate (shown
  // whether or not they're already assigned, so a picked name doesn't
  // change once added).
  // Self is IN the set: `/api/profiles/:sub` resolves a self-read without any
  // coach edge ("self-reads never need a coach relationship",
  // apps/api/src/lib/coachReadTarget.ts:22), so the caller's own name costs
  // one more call to an endpoint this hook already batches — and without it
  // the self row reads "Athlete", which is the caller's own name to nobody.
  const namedSubs = useMemo(() => {
    const s = new Set<string>();
    if (callerSub !== null) s.add(callerSub);
    for (const sub of assignedTo) s.add(sub);
    for (const sub of athleteRoster) s.add(sub);
    return [...s];
  }, [assignedTo, athleteRoster, callerSub]);

  const names = useProfileNames(apiFetch, namedSubs);

  const fallbackName = t("coach.dashboard.unnamedAthlete", { defaultValue: "Athlete" });
  // `sub: string | null` (not just `string`) so `nameOf(callerSub)` below
  // type-checks without a `?? ""` coercion — `callerSub` is `string | null`
  // for the type-coherence reasons noted on the prop above.
  const nameOf = (sub: string | null): string =>
    (sub !== null ? names[sub] : undefined) ?? fallbackName;
  // The self row's NAME is the caller's real name and its CAPTION says "You
  // (creator)". ⛔ Not the reverse: handing the sentence to `name` feeds it to
  // Avatar's `initialsFor`, which takes the first letter of the first and last
  // word — "You (creator)" renders as "Y(", "Tu (autore)" as "T)", in all five
  // locales, on every mount. Falls back to `fallbackName` only while the
  // profile read is in flight or if it fails.
  const selfName = nameOf(callerSub);

  const candidates = athleteRoster.filter(
    (sub) => sub !== callerSub && !assignedTo.includes(sub),
  );

  // PE-48 review fix #5 — a shape-only check, on ADD. It cannot catch a
  // typo'd domain ("marco@gmial.com" is a perfectly well-formed email) —
  // that half is the server's job (resolveAssignees returns email_not_found)
  // and showSaveError maps it to a named message at save time. This half
  // only catches what a coach can fix on the spot, before it ever reaches
  // Save: an empty local part, a missing "@", no dot in the domain.
  const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const addEmail = () => {
    const value = emailDraft.trim();
    if (!value) return;
    if (!EMAIL_SHAPE_RE.test(value)) {
      setEmailError(
        t(tk("invalidEmailFormat"), {
          defaultValue: "That doesn't look like a valid email address.",
        }),
      );
      return;
    }
    const lower = value.toLowerCase();
    if (assigneeEmails.some((e) => e.toLowerCase() === lower)) {
      setEmailError(null);
      setEmailDraft("");
      return;
    }
    onChangeAssigneeEmails([...assigneeEmails, value]);
    setEmailError(null);
    setEmailDraft("");
  };

  return (
    <Card theme={theme} style={styles.card}>
      <Text style={styles.title}>{t(gk("assignTo", "shareTo"))}</Text>
      <Text style={styles.help}>{t(gk("assignToHelp", "shareToHelp"))}</Text>

      <AssigneeRow
        theme={theme}
        name={selfName}
        caption={t(tk("assignSelf"))}
        removable={false}
      />
      {assignedTo
        .filter((sub) => sub !== callerSub)
        .map((sub) => (
          <AssigneeRow
            key={sub}
            theme={theme}
            name={nameOf(sub)}
            removable
            onRemove={() => onChangeAssignedTo(assignedTo.filter((s) => s !== sub))}
            removeLabel={t(tk("removeAssignee"))}
            confirmLabel={t(tk("removeAssigneeConfirm"), { defaultValue: "Remove access?" })}
            confirmHint={t(tk("removeAssigneeHint"), {
              defaultValue: "They'll lose this from their library. Tap again to remove.",
            })}
            spokenRestLabel={`${t(tk("removeAssignee"))} ${nameOf(sub)}`}
            spokenConfirmLabel={`${t(tk("removeAssigneeConfirm"), { defaultValue: "Remove access?" })} ${nameOf(sub)}`}
          />
        ))}
      {assigneeEmails.map((email) => (
        <AssigneeRow
          key={email}
          theme={theme}
          name={email}
          caption={t(tk("pendingAssigneeCaption"), { defaultValue: "Pending" })}
          removable
          onRemove={() => onChangeAssigneeEmails(assigneeEmails.filter((e) => e !== email))}
          removeLabel={t(tk("removeAssignee"))}
          confirmLabel={t(tk("removeAssigneeConfirm"), { defaultValue: "Remove access?" })}
          confirmHint={t(tk("removeAssigneeHint"), {
            defaultValue: "They'll lose this from their library. Tap again to remove.",
          })}
          spokenRestLabel={`${t(tk("removeAssignee"))} ${email}`}
          spokenConfirmLabel={`${t(tk("removeAssigneeConfirm"), { defaultValue: "Remove access?" })} ${email}`}
        />
      ))}

      {candidates.length > 0 ? (
        <>
          <Text style={styles.sectionLabel}>
            {t(gk("rosterSectionLabel", "shareSectionLabel"), {
              defaultValue: "From your roster",
            })}
          </Text>
          {candidates.map((sub) => (
            <View key={sub} style={styles.rosterRow}>
              <View style={styles.rosterRowName}>
                <AssigneeRow theme={theme} name={nameOf(sub)} removable={false} />
              </View>
              <Button
                theme={theme}
                compact
                label={t(tk("addAssignee"))}
                accessibilityLabel={`${t(tk("addAssignee"))} ${nameOf(sub)}`}
                onPress={() =>
                  // PE-48 review fix #1 — a NEW program starts with
                  // assignedTo: [] (programEdit.ts filters the "" sentinel
                  // out) and the self row above is a display-only fake: it
                  // is never actually IN assignedTo. Adding the first
                  // athlete without also putting callerSub in the array
                  // sends the server assignedTo:[athleteSub] alone, and
                  // resolveAssignees (apps/api/src/coachAssignment.ts) never
                  // re-adds the caller — the coach loses the program from
                  // their own library the moment it's saved. The Set dedupes
                  // the already-editing case (assignedTo may already
                  // contain callerSub) so this never doubles it up.
                  onChangeAssignedTo(
                    callerSub !== null
                      ? [...new Set([callerSub, ...assignedTo, sub])]
                      : [...assignedTo, sub],
                  )
                }
              />
            </View>
          ))}
        </>
      ) : athleteRoster.length > 0 ? (
        // PE-48 review fix #6 — the roster tab used to gate on
        // athleteRoster.length, which stays true once every athlete is
        // already assigned: an all-caps section title over nothing under
        // it (the owner's own visual-bench scene 4 hits exactly this —
        // athleteRoster and assignedTo are the same two subs). candidates
        // is roster-minus-self-minus-already-assigned, so THIS is the copy
        // for "you have a roster, but it's fully spoken for here".
        <Text style={styles.help}>
          {t(gk("rosterAllAssigned", "shareAllShared"), {
            defaultValue: "Everyone in your roster is already assigned.",
          })}
        </Text>
      ) : (
        <Text style={styles.help}>
          {t(gk("rosterEmpty", "shareEmpty"), {
            defaultValue: "No athletes in your roster yet.",
          })}
        </Text>
      )}

      <Text style={styles.sectionLabel}>
        {t(tk("emailSectionLabel"), { defaultValue: "By email" })}
      </Text>
      <View style={styles.emailRow}>
        <TextField
          theme={theme}
          style={styles.emailInput}
          value={emailDraft}
          onChangeText={(v) => {
            setEmailDraft(v);
            setEmailError(null);
          }}
          placeholder={t(tk("assigneePlaceholder"))}
          autoCapitalize="none"
          keyboardType="email-address"
          accessibilityLabel={t(tk("assignTo"))}
          // TextField.tsx's contract: `invalid` (a red border, RN has no a11y
          // invalid state) must always be paired with a caller-rendered,
          // visible error line — the non-colour cue. `styles.error` below
          // already renders `emailError` as that line; this just wires the
          // border to the same condition instead of leaving it colour-only
          // with nothing marking the field itself.
          invalid={!!emailError}
        />
        <Button
          theme={theme}
          compact
          label={t(tk("addAssignee"))}
          disabled={emailDraft.trim().length === 0}
          onPress={addEmail}
        />
      </View>
      {emailError ? <Text style={styles.error}>{emailError}</Text> : null}
    </Card>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: { gap: s.s3 },
    title: { color: p.ink, fontSize: t.sizes.md, fontWeight: "700" },
    help: { color: p.ink3, fontSize: t.sizes.sm },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.6,
      marginTop: s.s2,
    },
    rosterRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
    },
    rosterRowName: { flex: 1, minWidth: 0 },
    emailRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    emailInput: { flex: 1 },
    // Never colour-only: this text IS the a11y cue (there's no icon/shape
    // alongside it), same as every other inline error surface in this UI.
    error: { color: p.danger, fontSize: t.sizes.sm, fontWeight: "600" },
  });
}
